/**
 * B.12-R8 — OO «Расстановка операторов» (v8). Phase A: shell + read-only day
 * feed. Header = marquee HUD «OFFICE OF OPERATIONS · {ник} · UniBooking» +
 * DateSelector M.1. 6-tab sec-nav (icons; active shows the word). Real board
 * data via useOoDay. Rich cards / assign / AUTO / publish land in phases B+.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement, type SVGProps } from "react";
import { AppShell, MarqueeHeader } from "../layout";
import { DateSelector, type DateRange } from "../components/DateSelector";
import { useAuth } from "../auth";
import { OoGamesCount } from "./oo/OoGamesCount";
import { useOoDay, type BoardGame, type BoardOperator, type HallTimesMap } from "../hooks/useOoDay";
import { usePeerRequests, type PeerPair, type PeerReqItem } from "../hooks/usePeerRequests";
import { useLearningBoard, type LearningPlayer } from "../hooks/useLearningBoard"; import { PeerMenu, type PeerCtx } from "./oo/PeerMenu"; import { LearningRow } from "./oo/LearningRow"; /* 13.10: учебная игра — игроки и отчёты друг о друге */
import { useMcCatalog, type McItem } from "../hooks/useMcCatalog";
import { useExtraWork, type ExtraWorkGroup } from "../hooks/useExtraWork";
import { chipIsWarn, chipReasons, classify, type AssignMap, type HallAdminMap, type ScoringConfig } from "./oo/scoring"; import { OpDropdown } from "./oo/OpDropdown"; import { DayBusyCtx } from "./oo/dayBusy"; /* 16.10: «+» игры — свой файл (грейд, игры, пересечения, куда уже поставлен) */
import { autoSuggest } from "./oo/autoSuggest";
import { useAppData } from "../hooks/useAppData";
import { buildVenueMap, compareByVenue } from "@ub/shared";
import { AutoSheet } from "./oo/AutoSheet";
import { useChipDrag } from "./oo/useChipDrag";
import { useClampPop } from "./oo/useClampPop";
import { useLasso } from "./oo/useLasso";
import { BoardHelp } from "./oo/BoardHelp";
import { AdminStrip } from "./oo/AdminStrip";
import { McMiniRow } from "./oo/McMiniRow";
import { ClientInfoCard } from "../components/modal/ClientInfoCard";
import { DopWorkSheet } from "./oo/DopWorkSheet";
import { SchedulesView } from "./oo/SchedulesView";
import { OoDashboardView } from "./oo/OoDashboardView";
import { ClientBotPanel } from "./oo/ClientBotPanel";
import { HrHub } from "./oo/HrHub";

interface McAttach { key: string; mcId: string; mcName: string; duration: number; operatorIds: string[] }
import { todayBusiness, toBusinessDate, fromBusinessDate, fmtDateApi, addDays } from "../utils/date";
import { api } from "../api/client";
import { PlayersBadge } from "../components/shared";
import { ExtraWorkCard, ewDone } from "./oo/ExtraWorkCard"; import { canWholeSpan } from "./oo/canWhole"; /* 13.10: карточка допработы и «свободен ли» — вынесены */
import { EoConfigDicts } from "./eo/EoConfigDicts";
import {
  NavDash, NavRoster, NavGrid, NavBase, NavMkt, NavCab, NavBot, Plus, Xmark,
  ActAuto, ActCheck, ActSend, ActSpin, ActTrash, ActExpand, ActSort, ActDopWork, ActStudy, ActReload, ActWho, Diam, ChevDown, McStar, Gift, Money, PersonCard, WarnTri,
} from "./oo/v8icons";

type SortKey = "time" | "quest" | "qtime" | "branch";
const SORT_LABELS: Record<SortKey, string> = { time: "Время", quest: "Квест", qtime: "Время квеста", branch: "Филиал" };
import css from "./oo/board.module.css";

type ViewId = "dashboard" | "games" | "schedules" | "clientbot" | "base" | "mkt" | "hr" | "cab";
type IconC = (p: SVGProps<SVGSVGElement>) => ReactElement;

const TABS: { id: ViewId; label: string; Icon: IconC; menu?: string[] }[] = [
  { id: "dashboard", label: "Дашборд", Icon: NavDash },
  { id: "games", label: "Расстановка", Icon: NavRoster },
  { id: "schedules", label: "Графики", Icon: NavGrid },
  { id: "clientbot", label: "Бот для клиентов", Icon: NavBot },
  /* 11.09 (владелец): «Игры» — чистое количество отведённых за период календаря. */
  { id: "base", label: "База", Icon: NavBase, menu: ["Игры", "Омут", "Мир Квестов", "Журнал"] },
  { id: "mkt", label: "Маркетинг", Icon: NavMkt, menu: ["ID", "Импорт 1С"] },
  { id: "hr", label: "HR", Icon: PersonCard }, /* 26.08 (владелец): HR — отдельная вкладка, внутри субтабы */
  { id: "cab", label: "Расходники", Icon: NavCab }, /* 28.08 (владелец): дропдаун убран, внутри — панель расходников EO */
];

export function OoAssignScreen() {
  const { user } = useAuth();
  const [view, setView] = useState<ViewId>("games");
  const [date, setDate] = useState<Date>(() => todayBusiness());
  /* период шапочного календаря в HR (26.08): старт — текущая зарплатная неделя пт–чт,
     чтобы range-режим был ВИДЕН сразу (пустой диапазон выглядит как дневной) */
  const [hrRange, setHrRange] = useState<DateRange>(() => {
    const t = todayBusiness(); const s = addDays(t, -((t.getUTCDay() - 5 + 7) % 7));
    return { start: fromBusinessDate(s), end: fromBusinessDate(addDays(s, 6)) };
  });
  const [navMenu, setNavMenu] = useState<ViewId | null>(null); /* 26.10 (владелец): у «Игр» свой период — по умолчанию текущий месяц (у HR — неделя) */ const [gamesRange, setGamesRange] = useState<DateRange>(() => { const t = todayBusiness(); return { start: fromBusinessDate(new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), 1))), end: fromBusinessDate(new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0))) }; });
  const [stubLabel, setStubLabel] = useState<string | null>(null);
  const [salaryOpen, setSalaryOpen] = useState(false);
  const salaryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    document.documentElement.dataset.role = "oo";
  }, []);

  const onDateChange = useCallback((v: Date | DateRange) => {
    if (v instanceof Date) setDate(toBusinessDate(v));
  }, []);

  const nick = user?.nickname || user?.name || "guest";
  const board = useOoDay(date);

  function pickTab(id: ViewId, menu?: string[]) {
    if (menu) {
      setNavMenu((m) => (m === id ? null : id));
      setSalaryOpen(false);
    } else {
      setView(id);
      setStubLabel(null);
      setNavMenu(null);
    }
  }
  function pickMenuItem(tab: ViewId, item: string) {
    setView(tab);
    setStubLabel(item);
    setNavMenu(null);
    setSalaryOpen(false);
  }

  const openMenu = navMenu ? TABS.find((t) => t.id === navMenu) : null;

  return (
    <AppShell
      /* 27.08 (владелец): шапка и табы закреплены намертво — скроллит .main, не страница */
      lockViewport
      header={
        <MarqueeHeader
          roleLabel="OFFICE OF OPERATIONS"
          nick={nick}
          dateSelector={view === "hr" || (view === "base" && stubLabel === "Игры")
            /* 26.08 (владелец): в HR ШАПОЧНЫЙ календарь работает периодом (для «Зарплат»).
               key обязателен: mode у DateSelector — только НАЧАЛЬНЫЙ, без перемонта React
               реюзает инстанс и режим не переключается. */
            ? <DateSelector key={view === "hr" ? "hr" : "games"} mode="range" initialRange={view === "hr" ? hrRange : gamesRange} onChange={(v) => { if (!(v instanceof Date)) (view === "hr" ? setHrRange : setGamesRange)(v); }} />
            : <DateSelector key="day" mode="day" initialDate={fromBusinessDate(date)} onChange={onDateChange} />}
        />
      }
      topZone={
        <nav className={css.secNav}>
          {TABS.map(({ id, label, Icon, menu }) => {
            const active = menu ? stubLabel != null && view === id : view === id;
            return (
              <button
                key={id}
                type="button"
                className={`${css.snav} ${active ? css.active : ""}`}
                aria-label={label}
                onClick={() => pickTab(id, menu)}
              >
                <span className={css.ti}><Icon aria-hidden="true" /></span>
                <span className={css.tw}>{label}</span>
              </button>
            );
          })}
          {openMenu?.menu ? (
            <div className={css.navMenu}>
              {openMenu.menu.map((item) =>
                item === "Зарплаты" ? (
                  <div key={item}>
                    <button
                      type="button"
                      className={`${css.navMenuItem} ${css.hasSub}`}
                      onClick={() => pickMenuItem(openMenu.id, item)}
                      onPointerDown={() => { salaryTimer.current = setTimeout(() => setSalaryOpen(true), 450); }}
                      onPointerUp={() => { if (salaryTimer.current) clearTimeout(salaryTimer.current); }}
                      onPointerLeave={() => { if (salaryTimer.current) clearTimeout(salaryTimer.current); }}
                    >
                      {item}
                    </button>
                    {salaryOpen ? (
                      <button type="button" className={`${css.navMenuItem} ${css.navSub}`} onClick={() => pickMenuItem(openMenu.id, "Ставки (сетка)")}>
                        Ставки
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <button key={item} type="button" className={css.navMenuItem} onClick={() => pickMenuItem(openMenu.id, item)}>
                    {item}
                  </button>
                ),
              )}
            </div>
          ) : null}
        </nav>
      }
    >
      <div className={css.screen}>
        {view === "dashboard" ? (
          <OoDashboardView date={date} onBoardStale={board.reload} />
        ) : view === "games" ? (
          <GamesFeed board={board} date={date} />
        ) : view === "schedules" ? (
          <SchedulesView operators={board.operators} date={date} />
        ) : view === "clientbot" ? (
          <ClientBotPanel />
        ) : view === "hr" ? (
          <HrHub operators={board.operators} range={hrRange} onStaffChanged={board.reload} />
        ) : view === "cab" ? (
          <EoConfigDicts />
        ) : view === "base" && stubLabel === "Игры" ? (
          <OoGamesCount range={gamesRange} />
        ) : (
          <div className={css.stub}>
            <div>
              <div className={css.stubT}>В разработке</div>
              <div className={css.stubS}>{stubLabel ?? TABS.find((t) => t.id === view)?.label}</div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

/* ── Games feed (Phase B — interactive draft) ────────────────────────────── */

function GamesFeed({ board, date }: { board: ReturnType<typeof useOoDay>; date: Date }) {
  const { games, operators: allOps, hallAdmins, opById, nameOf, loading, error } = board; const operators = useMemo(() => allOps.filter((o) => !o.boardHidden), [allOps]);   // nameOf — архив 1С (В9): и уволенные; operators — без «не показывать в расстановке» (16.10): нет в дропдаунах, AUTO, «свободны»
  /* 25.09 (владелец): «квесты, которые проводятся на одной площадке (как форт и
     кальмар), должны стоять рядом». Связки и квесты уже загружены глобально —
     ни нового запроса, ни правки бэкенда не нужно. Если связок нет (или запрос
     упал), карта вырождается в сегодняшний алфавит, а не в поломку. */
  const app = useAppData();
  const venues = useMemo(() => buildVenueMap(app.quests, app.linkedGroups), [app.quests, app.linkedGroups]);
  const extra = useExtraWork(date);
  const peer = usePeerRequests(date); const learning = useLearningBoard(date, games);
  const [dopOpen, setDopOpen] = useState(false);
  /* 26.09 (владелец): «учёбка» — вызов на обучение без оплаты; куратор на ставке. */
  const [studyOpen, setStudyOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [draft, setDraft] = useState<AssignMap>({});
  const [hallDraft, setHallDraft] = useState<HallAdminMap>({}); const [hallTimesDraft, setHallTimesDraft] = useState<HallTimesMap>({});
  /* 27.08 (владелец, v2): дропдаун «кто чем занят СЕГОДНЯ» — игры, МК,
     адм залов, допработы этого дня из живого черновика; внизу — кто свободен.
     Обычный поповер (sortPop), закрывается тапом мимо. */
  const [whoOpen, setWhoOpen] = useState(false);
  const whoRef = useClampPop<HTMLDivElement>(whoOpen ? "who" : null);
  useEffect(() => { // закрытие тапом мимо
    if (!whoOpen) return;
    const onDown = (e: PointerEvent) => { const t = e.target as HTMLElement | null; if (!t?.closest?.("[data-whopop]")) setWhoOpen(false); };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [whoOpen]);
  const [branch, setBranch] = useState("all");
  const [openCard, setOpenCard] = useState<string | null>(null);

  const [mcTick, setMcTick] = useState(0);
  const mcCatalog = useMcCatalog(mcTick);
  const [mcDraft, setMcDraft] = useState<Record<string, McAttach[]>>({});

  // Re-seed the editable drafts whenever the board reloads.
  useEffect(() => { setDraft(board.assignMap); }, [board.assignMap]);
  useEffect(() => { setHallDraft(board.hallAdmins); }, [board.hallAdmins]); useEffect(() => { setHallTimesDraft(board.hallTimes); }, [board.hallTimes]); const dayBusy = useMemo(() => ({ hall: hallDraft, hallTimes: hallTimesDraft, mc: mcDraft, learn: learning.byBooking, extra: extra.items }), [hallDraft, hallTimesDraft, mcDraft, learning.byBooking, extra.items]);   /* 16.10: «+» — куда уже поставлен (oo/dayBusy) */
  useEffect(() => {
    const seed: Record<string, McAttach[]> = {};
    for (const g of board.games) {
      const ids = (g.mc ?? "").split(",").map((s) => s.trim()).filter(Boolean);
      const fromOps = board.mcByBooking[g.id] ?? {};
      const allIds = Array.from(new Set([...ids, ...Object.keys(fromOps)]));
      if (allIds.length === 0) continue;
      seed[g.id] = allIds.map((mcId) => {
        const cat = mcCatalog.find((m) => m.id === mcId);
        return { key: mcId, mcId, mcName: cat?.name ?? mcId, duration: cat?.duration ?? 0, operatorIds: fromOps[mcId] ?? [] };
      });
    }
    setMcDraft(seed);
  }, [board.games, board.mcByBooking, mcCatalog]);

  /* «кто чем занят сегодня»: игры (шорт цветом предоплаты + белое время),
     МК, адм залов, допработы из живых черновиков */
  const whoDay = useMemo(() => {
    type Part = { text: string; cls?: "pre" | "nopre" | "time" };
    if (!whoOpen) return { busy: [] as { name: string; acts: Part[][] }[], free: [] as string[] };
    const acts = new Map<string, Part[][]>();
    const add = (id: string, a: Part[]) => { if (id) acts.set(id, [...(acts.get(id) ?? []), a]); };
    for (const g of games) {
      const pre = g.prepaid || g.prepaidAmount > 0;
      for (const id of draft[g.id] ?? []) add(id, [{ text: g.shortName, cls: pre ? "pre" : "nopre" }, { text: g.time, cls: "time" }]);
      for (const a of mcDraft[g.id] ?? []) for (const id of a.operatorIds) add(id, [{ text: `МК ${a.mcName}` }]);
      for (const p of learning.byBooking[g.id] ?? []) add(p.id, [{ text: `играет ${g.shortName}` }, { text: g.time, cls: "time" }]); /* 13.10: игрок учебной игры занят ею */
    }
    for (const [br, ids] of Object.entries(hallDraft)) for (const opId of ids) add(opId, [{ text: `адм ${br.toUpperCase()}` }]);
    for (const e of extra.items) add(e.operatorId, [{ text: `доп ${e.timeFrom}–${e.timeTo}` }]);
    const busy = operators.filter((o) => acts.has(o.id))
      .map((o) => ({ name: o.name, acts: acts.get(o.id) ?? [] }))
      .sort((a, b) => a.name.localeCompare(b.name, "ru"));
    const free = operators.filter((o) => !acts.has(o.id)).map((o) => o.name).sort((a, b) => a.localeCompare(b, "ru"));
    return { busy, free };
  }, [whoOpen, games, draft, mcDraft, hallDraft, extra.items, operators, learning.byBooking]);

  const attachMc = useCallback((gid: string, item: McItem | { id: string; name: string; duration?: number }) => {
    setMcDraft((d) => {
      const list = d[gid] ?? [];
      if (list.some((a) => a.mcId === item.id)) return d;
      return { ...d, [gid]: [...list, { key: item.id, mcId: item.id, mcName: item.name, duration: ("duration" in item ? item.duration : 0) ?? 0, operatorIds: [] }] };
    });
  }, []);
  const detachMc = useCallback((gid: string, key: string) => {
    setMcDraft((d) => ({ ...d, [gid]: (d[gid] ?? []).filter((a) => a.key !== key) }));
  }, []);
  const addMcOp = useCallback((gid: string, key: string, opId: string) => {
    setMcDraft((d) => ({ ...d, [gid]: (d[gid] ?? []).map((a) => a.key === key && !a.operatorIds.includes(opId) ? { ...a, operatorIds: [...a.operatorIds, opId] } : a) }));
  }, []);
  const removeMcOp = useCallback((gid: string, key: string, opId: string) => {
    setMcDraft((d) => ({ ...d, [gid]: (d[gid] ?? []).map((a) => a.key === key ? { ...a, operatorIds: a.operatorIds.filter((x) => x !== opId) } : a) }));
  }, []);

  const addOp = useCallback((gameId: string, opId: string) => {
    setDraft((d) => {
      const list = d[gameId] ?? [];
      if (list.includes(opId)) return d;
      return { ...d, [gameId]: [...list, opId] };
    });
    setOpenCard(null);
  }, []);
  const removeOp = useCallback((gameId: string, opId: string) => {
    setDraft((d) => ({ ...d, [gameId]: (d[gameId] ?? []).filter((x) => x !== opId) }));
  }, []);
  /* 26.09: сохранённая и отправленная карточки одного слота — разные; человека, который уже есть
     в любой из них, второй раз не предлагаем (иначе две строки и два сообщения одному). */
  const slotOps = useCallback((g: ExtraWorkGroup): Set<string> => {
    const same = (x: ExtraWorkGroup) => x.training === g.training && x.timeFrom === g.timeFrom && x.timeTo === g.timeTo
      && (x.branchId ?? "") === (g.branchId ?? "") && x.description === g.description;
    return new Set(extra.groups.filter(same).flatMap((x) => x.ops.map((o) => o.operatorId)));
  }, [extra.groups]);
  /* «обвести» (20.08, Саша): дуга вокруг чипов → выделение → групповое КОПИРОВАНИЕ в другую игру */
  const lasso = useLasso({ onCopy: (ops, toId) => {
    /* 26.08 (владелец): лассо копирует и В допработы (data-gid="ew:{key}") */
    if (toId.startsWith("ew:")) {
      const g = extra.groups.find((x) => `ew:${x.key}` === toId);
      if (g) ops.filter((op) => !slotOps(g).has(op))
        /* 26.09: в карточку учёбки — УЧЕНИКОМ (kind), иначе человек получил бы оплачиваемую допработу */
        .forEach((op) => void extra.create({ operator_id: op, date: fmtDateApi(date), time_from: g.timeFrom, time_to: g.timeTo, description: g.description, branch_id: g.branchId ?? undefined, kind: g.training ? "training" : undefined, notify: false }));   /* 19.10: только сохранить — уйдёт по «Отправить» */
    } else ops.forEach((op) => addOp(toId, op));
  } });

  const [sendState, setSendState] = useState<"idle" | "sending" | "done" | "err">("idle");
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [sendInfo, setSendInfo] = useState<string | null>(null); /* 02.09: итог рассылки — кому ушло / не доставлено */
  const ewDrafts = extra.items.filter((e) => e.draft).length;   /* 27.09: сохранённые учёбки дня — тоже «не отправлено» */
  const unsentAll = board.unsent + ewDrafts;
  /* 26.09: итог «Отправить» сохранённой допработы/учёбки — плашкой внизу экрана (карточки ниже
     игр, строка итога наверху ушла бы из виду). «Не доставлено» висит, пока не закроешь. */
  const [ewInfo, setEwInfo] = useState<string | null>(null);
  const ewInfoT = useRef(0);
  const showEwInfo = useCallback((msg: string) => { /* 03.10: и «Не записаны (уволены)» с доски — висит, пока не закроют */
    const bad = /НЕ доставлено|не выполнена|уволены/.test(msg);
    setEwInfo((prev) => (!bad && prev && /НЕ доставлено|не выполнена|уволены/.test(prev) ? prev : msg));
    window.clearTimeout(ewInfoT.current);
    if (!bad) ewInfoT.current = window.setTimeout(() => setEwInfo((p) => (p === msg ? null : p)), 7000);
  }, []);
  const [holding, setHolding] = useState(false);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pubMenu, setPubMenu] = useState(false);
  const pubTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pubLong = useRef(false);

  const dirty = useMemo(
    () => JSON.stringify(normalize(draft)) !== JSON.stringify(normalize(board.assignMap)) || JSON.stringify(hallDraft) !== JSON.stringify(board.hallAdmins) || JSON.stringify(hallTimesDraft) !== JSON.stringify(board.hallTimes) || peer.dirty,   /* 13.10: и запросы отчётов — черновик доски */
    [draft, board.assignMap, hallDraft, board.hallAdmins, hallTimesDraft, board.hallTimes, peer.dirty],
  );

  const runAuto = useCallback((config?: ScoringConfig, useGender = true, requireExam = true) => {
    const res = autoSuggest({ bookings: board.games, operators, assignMap: draft, hallAdmins: board.hallAdmins, config, useGender, requireExam });
    setDraft(res.assignMap);
    setOpenCard(null);
  }, [board.games, operators, board.hallAdmins, draft]);

  const [autoSheetOpen, setAutoSheetOpen] = useState(false);
  const autoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoLong = useRef(false);
  const autoDown = useCallback(() => {
    autoLong.current = false;
    autoTimer.current = setTimeout(() => { autoLong.current = true; setAutoSheetOpen(true); }, 500);
  }, []);
  const autoUp = useCallback(() => {
    if (autoTimer.current) clearTimeout(autoTimer.current);
    if (!autoLong.current) runAuto();
  }, [runAuto]);
  const autoLeave = useCallback(() => { if (autoTimer.current) clearTimeout(autoTimer.current); }, []);

  /* 21.08 (Саша): галочка = только сохранить; отдельная стрелка = отправить операторам */
  const sendKind = useRef<"save" | "send">("save");
  const doPublish = useCallback(async (notify: boolean) => {
    sendKind.current = notify ? "send" : "save";
    setSendState("sending");
    setSendErr(null);
    const mcAssign = Object.entries(mcDraft).flatMap(([gid, attaches]) =>
      attaches.flatMap((a) => a.operatorIds.map((opId) => ({ booking_id: gid, mc_id: a.mcId, operator_id: opId }))),
    );
    const res = await board.publish(draft, hallDraft, mcAssign, notify, hallTimesDraft); const peerOk = res.ok ? await peer.flush() : true; /* 13.10: запросы отчётов «кто о ком» — по «Сохранить», не сразу */ if (res.fired || res.game1c) showEwInfo([res.fired, res.game1c].filter(Boolean).join(" · ")); /* 03.10 (владелец: «продолжи»): уволенных сервер не записал — кого, видно плашкой; доска уже перечитана. Архив 1С: «Игры 1С не меняются» — в той же плашке */
    if (res.ok && peerOk) {
      setSendState("done");
      /* 02.09 (владелец): ОО видит, кому ушло и кому НЕ доставлено */
      if (notify) {
        const n = res.notify; const failed = n?.failed ?? [];
        const msg = res.notifyError ? `Рассылка не выполнена: ${res.notifyError}`
          : n ? `Отправлено ${n.sent}${n.notified_names.length ? `: ${n.notified_names.join(", ")}` : ""}${failed.length ? ` · НЕ доставлено: ${failed.map((f) => `${f.name} (${f.reason})`).join(", ")}` : ""}${!n.sent && !failed.length ? " — изменений нет, все уже уведомлены" : ""}` : "";
        /* 27.09 (владелец: «учёбки должны отправляться как общей кнопкой, так и своей личной»):
           сохранённые учёбки дня уходят вместе с расстановкой; итог — в той же строке. */
        const drafts = extra.items.filter((e) => e.draft).map((e) => e.id);
        const ew = drafts.length ? await extra.send(drafts) : null;
        const all = [msg, ew ? `Допработы и учёбки: ${ew.msg}` : ""].filter(Boolean).join(" · ");
        setSendInfo(all || null);
        window.setTimeout(() => setSendInfo(null), failed.length || res.notifyError || (ew && !ew.ok) || /НЕ доставлено/.test(ew?.msg ?? "") ? 15000 : 7000);
      }
      setTimeout(() => setSendState("idle"), 2200);
    } else {
      setSendState("err");
      setSendErr(res.ok ? "Запросы отчётов не сохранились — нажмите ещё раз" : res.error ?? "Ошибка");
      setTimeout(() => setSendState("idle"), 3000);
    }
  }, [board, draft, hallDraft, hallTimesDraft, mcDraft, extra, showEwInfo, peer]);

  const trashDown = useCallback(() => {
    setHolding(true);
    holdTimer.current = setTimeout(() => {
      setDraft((d) => Object.fromEntries(Object.keys(d).map((k) => [k, []])));
      setHolding(false);
      setPubMenu(false);
    }, 1500);
  }, []);
  const trashUp = useCallback(() => {
    setHolding(false);
    if (holdTimer.current) clearTimeout(holdTimer.current);
  }, []);

  // Опубликовать: клик (надёжно срабатывает на тач) = публикация; удержание
  // 650мс → меню «Очистить расстановку» (эталон). onPointerUp больше НЕ
  // публикует — на iOS он не всегда срабатывает, из-за чего расстановка «не
  // отправлялась». Click после long-press подавляется флагом pubLong.
  const pubDown = useCallback(() => {
    pubLong.current = false;
    pubTimer.current = setTimeout(() => { pubLong.current = true; setPubMenu(true); }, 650);
  }, []);
  const pubUp = useCallback(() => { if (pubTimer.current) clearTimeout(pubTimer.current); }, []);
  const pubLeave = useCallback(() => { if (pubTimer.current) clearTimeout(pubTimer.current); }, []);
  const pubClick = useCallback(() => {
    if (pubTimer.current) clearTimeout(pubTimer.current);
    if (pubLong.current) { pubLong.current = false; return; } // это было удержание → меню, не сохранять
    setPubMenu(false);
    doPublish(false);
  }, [doPublish]);
  useEffect(() => {
    if (!pubMenu) return;
    const onDoc = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (!t?.closest?.("[data-sendwrap]")) setPubMenu(false);
    };
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, [pubMenu]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [expandAll, setExpandAll] = useState(false);
  /* 27.08 (владелец): сортировка — личная настройка, живёт в localStorage */
  const [sortKeys, setSortKeys] = useState<SortKey[]>(() => {
    try {
      const v = JSON.parse(localStorage.getItem("ub_board_sort") || "");
      const valid = (Array.isArray(v) ? v : []).filter((k): k is SortKey => k in SORT_LABELS);
      if (valid.length) return valid;
    } catch { /* нет сохранённой — дефолт */ }
    return ["time"];
  });
  const [sortOpen, setSortOpen] = useState(false);
  /* long-press по «Сорт.» — фильтр филиалов (26.08, владелец); строка пилюль — личная настройка */
  const [brMenu, setBrMenu] = useState(false);
  const [branchRowShown, setBranchRowShown] = useState(() => { try { return localStorage.getItem("ub_branch_row") === "1"; } catch { return false; } });
  const toggleBranchRow = () => setBranchRowShown((v) => { try { localStorage.setItem("ub_branch_row", v ? "0" : "1"); } catch { /* private mode */ } return !v; });
  const sortLp = useRef(0); const sortLpStart = useRef<{ x: number; y: number } | null>(null); const sortLpFired = useRef(false);
  const brMenuRef = useClampPop<HTMLDivElement>(brMenu || null);
  useEffect(() => { if (!brMenu) return; const onDown = (e: PointerEvent) => { if (!(e.target as HTMLElement | null)?.closest?.(`.${css.sortCtl}`)) setBrMenu(false); }; document.addEventListener("pointerdown", onDown, true); return () => document.removeEventListener("pointerdown", onDown, true); }, [brMenu]);

  const toggleExpand = useCallback((id: string) => {
    setExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);
  const toggleExpandAll = useCallback(() => {
    setExpandAll((on) => {
      const next = !on;
      setExpanded(next ? new Set(board.games.map((g) => g.id)) : new Set());
      return next;
    });
  }, [board.games]);
  const toggleSort = useCallback((k: SortKey) => {
    setSortKeys((keys) => {
      const next = keys.includes(k) ? keys.filter((x) => x !== k) : [...keys, k];
      try { localStorage.setItem("ub_board_sort", JSON.stringify(next)); } catch { /* private mode */ }
      return next;
    });
  }, []);

  if (error) return <div className={css.content}><div className={css.empty}>Ошибка: {error}</div></div>;
  if (loading) return <div className={css.content}><div className={css.empty}>Загрузка расстановки…</div></div>;
  /* 02.09 (владелец): дежурный DC и админы залов нужны и в день без игр */
  const strips = (
    <>
    {(board.allBranches.length || board.branches.length) ? (
      <AdminStrip
        branches={board.allBranches.length ? board.allBranches : board.branches}
        hall={hallDraft} hallTimes={hallTimesDraft} hallState={board.hallState}
          dcs={board.dcDuties} onAddDc={(id) => void board.addDc(id).catch(() => {})} onRemoveDc={(id) => void board.removeDc(id).catch(() => {})}
          onSetDcTime={(id, f, t) => void board.setDcTime(id, f, t).catch(() => {})}
        operators={operators}
        opById={opById} nameOf={nameOf}
        onSet={(br, ids) => setHallDraft((h) => { const n = { ...h }; if (ids.length) n[br] = ids; else { delete n[br]; setHallTimesDraft((t) => { const m = { ...t }; delete m[br]; return m; }); } return n; })}
        onSetTime={(br, from, to) => setHallTimesDraft((t) => { const m = { ...t }; if (from || to) m[br] = { from, to }; else delete m[br]; return m; })}
      />
    ) : null}
    </>
  );
  /* 02.09 (владелец): строка кнопок ОО (сорт/кто занят/AUTO/сохранить/отправить) — всегда, даже без игр */
  const actions = (
    <>
    <div className={css.actionsRow}>
      <div className={css.sortCtl}>
        <button type="button" className={`${css.sortTrigger} ${sortOpen || brMenu ? css.on : ""}`}
          aria-label="Сортировка" title="Тап — сортировка; long-press — фильтр филиалов"
          onPointerDown={(e) => { sortLpFired.current = false; sortLpStart.current = { x: e.clientX, y: e.clientY }; sortLp.current = window.setTimeout(() => { sortLpFired.current = true; setSortOpen(false); setBrMenu(true); }, 450); }}
          onPointerMove={(e) => { const s = sortLpStart.current; if (s && (Math.abs(e.clientX - s.x) > 8 || Math.abs(e.clientY - s.y) > 8)) window.clearTimeout(sortLp.current); }}
          onPointerUp={() => window.clearTimeout(sortLp.current)} onPointerLeave={() => window.clearTimeout(sortLp.current)} onContextMenu={(e) => e.preventDefault()}
          onClick={() => { if (sortLpFired.current) { sortLpFired.current = false; return; } setBrMenu(false); setSortOpen((o) => !o); }}>
          <ActSort />
        </button>
        {sortOpen ? (
          <div className={css.sortPop}>
            {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => {
              const idx = sortKeys.indexOf(k);
              return (
                <button key={k} type="button" className={`${css.sortItem} ${idx >= 0 ? css.on : ""}`} onClick={() => toggleSort(k)}>
                  {SORT_LABELS[k]}{idx >= 0 ? <span className={css.ord}>{idx + 1}</span> : null}
                </button>
              );
            })}
          </div>
        ) : null}
        {brMenu ? (
          <div className={css.sortPop} ref={brMenuRef}>
            {["all", ...board.branches].map((b) => (
              <button key={b} type="button" className={`${css.sortItem} ${branch === b ? css.on : ""}`} onClick={() => { setBranch(b); setBrMenu(false); }}>
                {b === "all" ? "Все филиалы" : b}{branch === b ? <span className={css.ord}>✓</span> : null}
              </button>
            ))}
            <button type="button" className={css.sortItem} onClick={() => { toggleBranchRow(); setBrMenu(false); }}>
              {branchRowShown ? "Скрыть строку фильтров" : "Показать отдельной строкой"}
            </button>
          </div>
        ) : null}
      </div>
      <span className={css.actSpacer} />
      <span className={css.sortCtl} data-whopop>
        <button type="button" className={`${css.actIcon} ${whoOpen ? css.on : ""}`} title="Кто чем занят сегодня" aria-label="Кто чем занят сегодня" onClick={() => setWhoOpen((v) => !v)}>
          <ActWho />
        </button>
        {whoOpen ? (
          <div className={`${css.sortPop} ${css.whoPop}`} ref={whoRef}>
            {whoDay.busy.length === 0 && whoDay.free.length === 0 ? <div className={css.wlEmpty}>Пусто</div> : null}
            {whoDay.busy.map((r) => (
              <div key={r.name} className={css.wlRow}>
                <span className={css.wlName}>{r.name}</span>
                <span className={css.wlQ}>
                  {r.acts.map((a, i) => (
                    <span key={i}>
                      {i > 0 ? " · " : ""}
                      {a.map((p, j) => (
                        <span key={j} className={p.cls === "pre" ? css.wlPre : p.cls === "nopre" ? css.wlNoPre : p.cls === "time" ? css.wlTime : undefined}>
                          {j > 0 ? " " : ""}{p.text}
                        </span>
                      ))}
                    </span>
                  ))}
                </span>
              </div>
            ))}
            {whoDay.free.length ? (
              <div className={css.wlFree}><span className={css.wlFreeLbl}>Свободны:</span> {whoDay.free.join(", ")}</div>
            ) : null}
          </div>
        ) : null}
      </span>
      <button type="button" className={css.actIcon} title="Обновить расстановку" aria-label="Обновить расстановку" onClick={() => { board.reload(); setMcTick((t) => t + 1); }}>
        <ActReload />
      </button>
      <button type="button" className={`${css.actIcon} ${css.dop}`} title="Доп.работа" aria-label="Доп.работа" onClick={() => setDopOpen(true)}>
        <ActDopWork />
      </button>
      <button type="button" className={`${css.actIcon} ${css.study}`} title="Учёбка — без оплаты, куратор на ставке" aria-label="Учёбка" onClick={() => setStudyOpen(true)}>
        <ActStudy />
      </button>
      <button type="button" className={`${css.actIcon} ${css.expandAll} ${expandAll ? css.on : ""}`} title="Развернуть все" aria-label="Развернуть все" onClick={toggleExpandAll}>
        <ActExpand />
      </button>
      <span className={css.sortCtl}>
        <button type="button" className={`${css.actIcon} ${css.helpBtn} ${helpOpen ? css.on : ""}`} title="Справка" aria-label="Справка" onClick={() => setHelpOpen((o) => !o)}>?</button>
        {helpOpen ? <BoardHelp onClose={() => setHelpOpen(false)} /> : null}
      </span>
      <button
        type="button"
        className={`${css.actIcon} ${css.auto}`}
        title="AUTO (тап — пресет; удержание — настройки)"
        aria-label="Авторасстановка"
        onPointerDown={autoDown}
        onPointerUp={autoUp}
        onPointerLeave={autoLeave}
      >
        <ActAuto />
      </button>
      <span className={css.sendWrap} data-sendwrap>
        <button
          type="button"
          className={`${css.actIcon} ${css.publish} ${sendState === "done" ? css.done : ""} ${sendState === "err" ? css.err : ""} ${sendState === "sending" ? css.sending : ""}`}
          onPointerDown={pubDown}
          onPointerUp={pubUp}
          onPointerLeave={pubLeave}
          onClick={pubClick}
          disabled={sendState === "sending"}
          aria-label="Сохранить расстановку"
          title={sendState === "sending" ? "Сохраняю…" : sendState === "done" ? "Готово" : sendState === "err" ? "Ошибка" : dirty ? "Есть несохранённые изменения — сохранить" : "Сохранить без отправки (удержание — очистить)"}
        >
          {/* 28.08 (владелец): вместо надписи «черновик» — «!» на галочке при несохранённом */}
          {sendState === "sending" ? <ActSpin /> : dirty && sendState === "idle" ? <span className={css.bangIco}>!</span> : <ActCheck />}
        </button>
        {/* 02.09 (владелец): сохранили, но не отправили — «!» на «Отправить» (считает сервер, переживает перезагрузку) */}
        <button type="button" className={`${css.actIcon} ${css.sendOps}`} onClick={() => doPublish(true)} disabled={sendState === "sending"} aria-label="Отправить расстановку операторам"
          title={!dirty && unsentAll > 0 ? `Не отправлено операторам: ${[board.unsent ? `${board.unsent} назначений` : "", ewDrafts ? `${ewDrafts} в допработах и учёбках` : ""].filter(Boolean).join(", ")} — отправить` : "Отправить расстановку операторам"}>
          {!dirty && unsentAll > 0 && sendState === "idle" ? <span className={css.bangIco}>!</span> : <ActSend />}
        </button>
        {pubMenu ? (
          <div className={css.pubMenu}>
            <button
              type="button"
              className={`${css.pubTrash} ${holding ? css.holding : ""}`}
              title="Очистить расстановку (удерживать 1.5с)"
              onPointerDown={trashDown}
              onPointerUp={trashUp}
              onPointerLeave={trashUp}
            >
              <span className={css.holdFill} />
              <span className={css.icoSvg}><ActTrash /></span>
              <span className={css.ptLbl}>Очистить расстановку</span>
            </button>
          </div>
        ) : null}
      </span>
    </div>
    {sendState === "sending" ? <div className={css.actHint}>{sendKind.current === "send" ? "Отправляю расстановку операторам…" : "Сохраняю расстановку…"}</div>
      : sendState === "done" ? <div className={`${css.actHint} ${css.actHintOk}`}>{sendKind.current === "send" ? "✓ Расстановка отправлена операторам" : "✓ Сохранено (операторам не отправлено)"}</div>
      : sendErr ? <div className={`${css.actHint} ${css.actHintErr}`}>Ошибка: {sendErr}</div>
      : null}
    {sendInfo ? <div className={`${css.actHint} ${/НЕ доставлено|не выполнена/.test(sendInfo) ? css.actHintErr : ""}`}>{sendInfo}</div> : null}
    </>
  );
  /* 09.09: панель доп.работы нужна и в день без игр — уборка и ремонт как раз тогда. */
  const dopSheet = (
    <DopWorkSheet
      open={dopOpen}
      onClose={() => setDopOpen(false)}
      date={date}
      operators={operators}
      branches={board.allBranches.length ? board.allBranches : board.branches}   /* 13.10: все филиалы, а не только с играми дня — в день без игр был один «любой» */
      games={games}
      assignMap={draft}
      onCreateMany={extra.createMany}
    />
  );
  const studySheet = (
    <DopWorkSheet mode="study" open={studyOpen} onClose={() => setStudyOpen(false)} date={date}
      operators={operators} branches={board.allBranches.length ? board.allBranches : board.branches} games={games} assignMap={draft} onCreateMany={extra.createMany} />
  );
  /* 26.09: карточки допработ собираем ДО выхода «нет игр» — в день без игр их не было видно
     вовсе, и сохранённую учёбку было бы нечем отправить. */
  const ewShown = [...extra.groups].sort((a, b) => a.timeFrom.localeCompare(b.timeFrom));
  const ewCards = ewShown.map((e) => <ExtraWorkCard key={`ew-${e.key}`} group={e} operators={operators}
    taken={slotOps(e)} done={ewDone(e, date)} study={{ date: fmtDateApi(date), branches: board.allBranches.length ? board.allBranches : board.branches, groups: extra.groups, onDone: (msg) => { showEwInfo(msg); extra.reload(); } }} /* 13.10: ✎ правка учёбки */
    lassoSel={lasso.selected} lassoDown={lasso.groupDown}
    onRemoveOp={(rowId) => extra.remove(rowId, e.training)}
    onRemoveAll={(g) => void Promise.all(g.ops.map((o) => api(`/api/extra-work/${o.rowId}${g.training ? "?notify=cancel" : ""}`, { method: "DELETE" }).catch(() => {}))).then(extra.reload)}
    onAddOp={(g, opId) => void extra.create({ operator_id: opId, date: fmtDateApi(date), time_from: g.timeFrom, time_to: g.timeTo, description: g.description, branch_id: g.branchId ?? undefined, kind: g.training ? "training" : undefined, notify: false })}   /* 19.10: «+» — только сохранить, сообщение — по «Отправить» */
    onSend={(g) => extra.send(g.ops.map((o) => o.rowId)).then((r) => { showEwInfo(r.msg); return r.ok; })}
    onPatch={(g, patch) => void Promise.all(g.ops.map((o) => api(`/api/extra-work/${o.rowId}`, { method: "PUT", body: patch }).catch(() => {}))).then(extra.reload)}
    onFactOk={(rowId) => void api(`/api/extra-work/${rowId}`, { method: "PUT", body: { fact_ok: 1 } }).catch(() => {}).then(extra.reload)}
    onRate={(rowId, rate) => void api(`/api/extra-work/${rowId}`, { method: "PUT", body: { hourly_rate: rate, rate_override: null } }).catch(() => {}).then(extra.reload)}
  />);
  const ewToast = ewInfo ? (
    <button type="button" className={`${css.ewToast} ${/НЕ доставлено|не выполнена|уволены/.test(ewInfo) ? css.ewToastErr : ""}`}
      onClick={() => setEwInfo(null)} title="Закрыть">{ewInfo}</button>
  ) : null;
  if (games.length === 0) return <div className={css.content}>{actions}{strips}<div className={css.empty}>Нет игр на этот день</div>{ewCards}{ewToast}{dopSheet}{studySheet}</div>;

  const filtered = branch === "all" ? games : games.filter((g) => g.branchId === branch);
  const shown = [...filtered].sort((a, b) => {
    for (const k of sortKeys) {
      let c = 0;
      if (k === "time") c = a.sortTime.localeCompare(b.sortTime);
      else if (k === "quest") c = compareByVenue(a, b, venues);
      else if (k === "branch") c = a.branchId.localeCompare(b.branchId, "ru");
      else if (k === "qtime") c = a.duration - b.duration;
      if (c !== 0) return c;
    }
    return a.sortTime.localeCompare(b.sortTime);
  });

  /* допработы — ПОД всеми играми и независимо от филиала (20.08, Саша) */
  const feed = shown.map((g) => ({ t: g.time, kind: "game" as const, g }));

  return (
    <div {...lasso.surfaceProps} className={`${css.content} ${lasso.surfaceProps.className}`}>
      {lasso.overlay}
      {actions}

      {strips}

      {board.branches.length > 1 && branchRowShown ? (
        <div className={css.branchRow}>
          {["all", ...board.branches].map((b) => (
            <button key={b} type="button" className={`${css.branchPill} ${branch === b ? css.on : ""}`} onClick={() => setBranch(b)}>
              {b === "all" ? "Все" : b}
            </button>
          ))}
        </div>
      ) : null}

      <DayBusyCtx.Provider value={dayBusy}>{feed.map((item) => (
          <GameCard
            key={item.g.id}
            game={item.g}
            date={date}
            crew={draft[item.g.id] ?? []}
            allGames={games}
            draft={draft}
            operators={operators}
            hallAdmins={hallAdmins}
            opById={opById} boardName={nameOf}
            dropOpen={openCard === item.g.id}
            expanded={expanded.has(item.g.id)}
            mc={mcDraft[item.g.id] ?? []}
            mcCatalog={mcCatalog}
            peerPairs={peer.byBooking[item.g.id] ?? []} players={learning.byBooking[item.g.id] ?? []}
            onPeerAdd={(reqs) => peer.add(item.g.id, reqs)}
            onPeerRemove={(a, s) => peer.remove(item.g.id, a, s)}
            onToggleExpand={() => toggleExpand(item.g.id)}
            onToggleDrop={() => setOpenCard((c) => (c === item.g.id ? null : item.g.id))}
            onAdd={(opId) => addOp(item.g.id, opId)}
            onRemove={(opId) => removeOp(item.g.id, opId)}
            onAttachMc={(it) => attachMc(item.g.id, it)}
            onDetachMc={(key) => detachMc(item.g.id, key)}
            onAddMcOp={(key, opId) => addMcOp(item.g.id, key, opId)}
            onRemoveMcOp={(key, opId) => removeMcOp(item.g.id, key, opId)}
            lassoSel={lasso.selected} lassoDown={lasso.groupDown} acceptedSet={board.acceptedSet}
          />
      ))}</DayBusyCtx.Provider>
      {ewCards}
      {ewToast}

      <AutoSheet
        open={autoSheetOpen}
        onClose={() => setAutoSheetOpen(false)}
        onRun={(config, useGender, requireExam) => { runAuto(config, useGender, requireExam); setAutoSheetOpen(false); }}
      />
      {dopSheet}
      {studySheet}
    </div>
  );
}

/* ── R8.6 МК и доп.услуги — блок цвета МК (--mc, 11.10), multi-operator ──── */
function McBlock({
  mc, branchId, catalog, operators, candidates, opById, boardName, onAttach, onDetach, onAddOp, onRemoveOp,
}: {
  mc: McAttach[];
  branchId: string;
  catalog: McItem[];
  operators: BoardOperator[];
  candidates: (operators: BoardOperator[], mcId: string, taken: string[]) => BoardOperator[];
  opById: ReturnType<typeof useOoDay>["opById"]; /** архив 1С (В9): подпись и уволенного */ boardName: (id: string) => string;
  onAttach: (item: McItem | { id: string; name: string }) => void;
  onDetach: (key: string) => void;
  onAddOp: (key: string, opId: string) => void;
  onRemoveOp: (key: string, opId: string) => void;
}) {
  const [catOpen, setCatOpen] = useState(false);
  const [opPick, setOpPick] = useState<string | null>(null); const mcOplistRef = useClampPop<HTMLDivElement>(opPick);
  const [custom, setCustom] = useState("");
  /* 06.09 (владелец): филиал услуги — порядок, не фильтр: свои и общесетевые первыми, остальные — следом */
  const own = (m: { branchId: string }) => !branchId || !m.branchId || m.branchId.toLowerCase() === branchId.toLowerCase();
  const branchCat = [...catalog].sort((a, b) => Number(!own(a)) - Number(!own(b)));

  return (
    <div className={css.mkBlock}>
      <div className={css.mkHead}><span className={css.icoSvg}><McStar /></span><span>МК и доп.услуги</span></div>
      {mc.map((a) => (
        <div key={a.key} className={css.mkItem}>
          <span className={css.mkIco} aria-hidden="true">{a.duration ? <McStar /> : <Gift />}</span>
          <span className={css.mkName}>{a.mcName}</span>
          {a.duration ? <span className={css.mkDur}>{a.duration}м</span> : null}
          <button type="button" className={css.mkDetach} aria-label="Открепить" onClick={() => onDetach(a.key)}><Xmark /></button>
          <div className={css.mkOp}>
            {a.operatorIds.map((opId) => {
              const mcRate = opById.get(opId)?.mcRates?.[a.mcId] ?? 0;
              return (
              <span key={opId} className={`${css.chip} ${css.mkchip} ${mcRate > 0 ? "" : css.mkNoRate}`}
                title={mcRate > 0 ? `Ставка за МК: ₽${mcRate}` : "Ставка за этот МК не задана — оплата будет ₽0"}>
                <span className={css.chipNm}>{boardName(opId)}</span>
                <span className={css.mkRate}>{mcRate > 0 ? `₽${mcRate}` : "₽0!"}</span>
                <button type="button" className={css.chipX} aria-label="Убрать" onClick={() => onRemoveOp(a.key, opId)}><Xmark /></button>
              </span>);
            })}
            <div className={css.addOpWrap}>
              <button type="button" className={`${css.mkAddop} ${opPick === a.key ? css.on : ""}`} aria-label="Добавить оператора" onClick={() => setOpPick((p) => (p === a.key ? null : a.key))}><Plus /></button>
              {opPick === a.key ? (
                <div className={css.mkOplist} ref={mcOplistRef}>
                  {candidates(operators, a.mcId, a.operatorIds).map((o) => (
                    <button key={o.id} type="button" className={css.opItem} onClick={() => { onAddOp(a.key, o.id); setOpPick(null); }}>
                      <span className={css.opName}>{o.name}</span>
                      {o.mcSkills.includes(a.mcId) ? <span className={`${css.opMark} ${css.ok} ${css.opPlus}`}>+</span> : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ))}
      <div className={css.addOpWrap}>
        <button type="button" className={`${css.mkAttach} ${catOpen ? css.on : ""}`} onClick={() => setCatOpen((o) => !o)}>+ прикрепить МК / услугу</button>
        {catOpen ? (
          <div className={css.mkCatalog}>
            <div className={css.catSec}>Мастер-классы</div>
            <div className={css.catGrid}>
              {branchCat.filter((m) => !mc.some((a) => a.mcId === m.id)).length === 0 ? <span className={css.catEmpty}>— нет —</span> : null}
              {branchCat.filter((m) => !mc.some((a) => a.mcId === m.id)).map((m) => (
                <button key={m.id} type="button" className={css.catIt} onClick={() => { onAttach(m); setCatOpen(false); }}>{m.name}{m.duration ? ` · ${m.duration}м` : ""}</button>
              ))}
            </div>
            <div className={css.catSec}>Доп.услуги</div>
            <div className={css.catCustom}>
              <input className={css.catInput} placeholder="Своя услуга…" value={custom} onChange={(e) => setCustom(e.target.value)} />
              <button type="button" className={css.catAdd} onClick={() => { if (custom.trim()) { onAttach({ id: `svc_${Date.now()}`, name: custom.trim() }); setCustom(""); setCatOpen(false); } }}>Добавить</button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function GameCard({
  game, crew, allGames, draft, operators, hallAdmins, opById, boardName, dropOpen, expanded, mc, mcCatalog, lassoSel, lassoDown,
  peerPairs, onPeerAdd, onPeerRemove, players,
  onToggleExpand, onToggleDrop, onAdd, onRemove, onAttachMc, onDetachMc, onAddMcOp, onRemoveMcOp, acceptedSet,
}: {
  game: BoardGame; date: Date; crew: string[];
  /** принятые оператором игры (26.08) — зелёная рамка чипа */ acceptedSet: Set<string>;
  allGames: BoardGame[];
  draft: AssignMap;
  operators: BoardOperator[];
  hallAdmins: HallAdminMap;
  opById: ReturnType<typeof useOoDay>["opById"]; /** архив 1С (В9, 16.10): подпись того, кого нет в списке доски (уволенные) — board.nameOf */ boardName: (id: string) => string;
  dropOpen: boolean;
  lassoSel: Set<string>; lassoDown: (e: React.PointerEvent) => void;
  expanded: boolean;
  mc: McAttach[];
  mcCatalog: McItem[];
  peerPairs: PeerPair[]; /** 13.10: кто играет в учебной игре (не состав) */ players: LearningPlayer[];
  onPeerAdd: (requests: PeerReqItem[]) => void;
  onPeerRemove: (authorId: string, subjectId: string | null) => void;
  onToggleExpand: () => void;
  onToggleDrop: () => void;
  onAdd: (opId: string) => void;
  onRemove: (opId: string) => void;
  onAttachMc: (item: McItem | { id: string; name: string }) => void;
  onDetachMc: (key: string) => void;
  onAddMcOp: (key: string, opId: string) => void;
  onRemoveMcOp: (key: string, opId: string) => void;
}) {
  const filled = crew.length; const need = game.operatorsNeeded;
  const statusCls = filled === 0 ? css.crewDanger : filled < need ? css.crewWarn : css.crewOk;
  const crewGenders = crew.map((id) => opById.get(id)?.gender ?? "").filter(Boolean);
  /* МК-кандидаты (27.08, владелец): показываем ВСЕХ, порядок — кто на этой
     игре → кто сегодня на этом филиале → остальные; внутри группы умеющие
     вести МК выше («✓ ведёт» остаётся меткой), дальше по алфавиту. */
  const branchDayIds = useMemo(() => {
    const s = new Set<string>();
    for (const g of allGames) if (g.branchId === game.branchId) for (const id of draft[g.id] ?? []) s.add(id);
    return s;
  }, [allGames, draft, game.branchId]);
  const mcCand = useCallback((ops: BoardOperator[], mcId: string, taken: string[]) => {
    const crewSet = new Set(crew);
    const rank = (o: BoardOperator) => (crewSet.has(o.id) ? 0 : branchDayIds.has(o.id) ? 1 : 2);
    return ops
      .filter((o) => !taken.includes(o.id))
      .sort((a, b) => rank(a) - rank(b)
        || Number(b.mcSkills.includes(mcId)) - Number(a.mcSkills.includes(mcId))
        || a.name.localeCompare(b.name, "ru"));
  }, [crew, branchDayIds]);
  const [showClient, setShowClient] = useState(false); const [chipMenu, setChipMenu] = useState<string | null>(null);
  const closeChipMenu = useCallback(() => setChipMenu(null), []);
  /* long-press = меню; смахнуть = удалить (перенос-«вырезать» удалён 21.08 — конфликтовал со всем) */
  const { chipDown, chipMove, chipRelease } = useChipDrag({ onMenu: setChipMenu, onSwipeAway: onRemove });
  /* меню отчётов (вынесено в oo/PeerMenu 13.10): кандидаты «на филиале» — те же branchDayIds; имена — и игроков */
  const nameOf = (id: string) => opById.get(id)?.name ?? players.find((p) => p.id === id)?.name ?? boardName(id);
  const peerCtx: PeerCtx = { crew, players, branchIds: branchDayIds, nameOf, onPeerAdd };

  return (
    <article className={`${css.gameLite} ${statusCls} ${/мир.?кв|mirkv/i.test(game.source ?? "") ? css.mirkvIn : ""}`} data-gid={game.id}>
      <div className={css.glHead} onClick={onToggleExpand}>
        <span className={css.glTime}>{game.time}</span>
        {/* 26.08 (владелец): цвет названия = предоплата (зелёный есть / оранжевый нет) */}
        <span className={`${css.glQuest} ${game.prepaid ? css.glQPay : css.glQNoPay}`}>{game.shortName}</span>{game.comment ? <span className={css.glComm} title={game.comment}>{game.comment}</span> : <span style={{ flex: 1 }} />}
        {game.tea ? <span className={`${css.badge} ${css.tea}`} title="Чайная"><svg width="11" height="11" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="square" aria-hidden="true"><path d="M2 5h8v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5z" /><path d="M10 6h1.5a1.5 1.5 0 0 1 0 3H10" /><path d="M5 2v1M7 2v1" /></svg></span> : null}
        {game.isBirthday ? <span className={`${css.badge} ${css.dr}`}>др</span> : null}
        <span className={css.glRight}>
          {/* 05.09 (владелец): стандартный бейдж игроков; карточка общая с «Инфо о клиенте» ниже — бейдж только зовёт её */}
          <PlayersBadge count={game.players} bookingId={Number(game.id)} fallbackName={game.client ?? undefined} size="md" onOpen={() => setShowClient(true)} />
          <span className={css.rhAddWrap} onClick={(e) => e.stopPropagation()}>
            <button type="button" className={`${css.rhAdd} ${dropOpen ? css.on : ""}`} aria-label="Добавить оператора" onClick={(e) => { e.stopPropagation(); onToggleDrop(); }}><Plus /></button>
            {dropOpen ? <OpDropdown game={game} crew={crew} allGames={allGames} draft={draft} operators={operators} hallAdmins={hallAdmins} onPick={onAdd} onClose={onToggleDrop} /> : null}
          </span>
        </span>
      </div>

      {/* полный профиль клиента (визиты, прошлые брони, комментарии, отчёты операторов) — оверлей, закрывается тапом где угодно */}
      {showClient ? <ClientInfoCard bookingId={Number(game.id)} fallbackName={game.client ?? undefined} onClose={() => setShowClient(false)} /> : null}

      {expanded ? (
        <div className={css.glDetails}>
          <div className={css.rbPrepay}>
            <span className={css.rbAmt}>
              <span className={css.icoSvg}><Money /></span>Предоплата <span className={css.rbSum}>{game.prepaidAmount.toLocaleString("ru")}₽</span>
            </span>
            <button type="button" className={css.btnClient} onClick={() => setShowClient((s) => !s)}>
              <span className={css.icoSvg}><PersonCard /></span>Инфо о клиенте
            </button>
          </div>
          {game.comment ? (
            <div className={css.rbComment}>
              <span className={`${css.icoSvg} ${css.rbWarn}`}><WarnTri /></span>{game.comment}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={`${css.glCrew} ${expanded ? css.glCrewExp : ""}`}>
        {expanded ? <span className={css.crewLbl}>Состав</span> : null}
        {crew.map((id) => {
          const op = opById.get(id);
          /* 26.08 (владелец): не может провести всю игру по графику → имя красным */
          const cantWhole = op ? !canWholeGame(op, game) : false;
          const warn = op ? chipIsWarn(op, game, crewGenders) || cantWhole : false;
          const reasons = op ? [...chipReasons(op, game, crewGenders), ...(cantWhole ? ["по графику не может всю игру"] : [])] : [];
          const mcMine = mc.filter((a) => a.operatorIds.includes(id)); // его МК — прямо в чипе (20.08, Саша)
          return (
            <span key={id} className={css.chipWrap}>
              <span
                data-op={id}
                className={`${css.glChip} ${warn ? css.warn : ""} ${acceptedSet.has(`${game.id}_${id}`) ? css.acc : ""} ${lassoSel.has(`${game.id}:${id}`) ? css.sel : ""}`}
                onPointerDown={(e) => (lassoSel.has(`${game.id}:${id}`) ? lassoDown(e) : chipDown(id, e))} onPointerMove={(e) => chipMove(id, e)}
                onPointerUp={chipRelease} onPointerCancel={chipRelease} onContextMenu={(e) => e.preventDefault()}
              >
                {op?.name ?? boardName(id)}
                {mcMine.length ? <span className={css.chipMc}>{mcMine.map((a) => a.mcName.replace(/^МК\s+/i, "")).join(", ")}</span> : null}
              </span>
              {chipMenu === id ? <PeerMenu {...peerCtx} personId={id} kind="crew" reasons={reasons} onClose={closeChipMenu} /> : null}
            </span>
          );
        })}
        {expanded && filled < need ? <button type="button" className={css.addCrew} onClick={onToggleDrop}>+ добавить</button> : null}
        {/* peer-заявки — в той же строке, компактно без ✍; wrap уносит только то, что реально не влезло */}
        {peerPairs.map((p) => (
          <span key={`${p.authorId}|${p.subjectId ?? ""}`} className={css.peerRow}>
            <span className={css.peerA}>{p.authorName}</span><span className={css.peerArr}>→</span><span className={css.peerS}>{p.subjectId ? `о ${p.subjectName}` : "общий"}</span>
            <button type="button" className={css.peerRowX} aria-label="Отменить запрос" onClick={() => onPeerRemove(p.authorId, p.subjectId)}><Xmark /></button>
          </span>
        ))}
        {/* МК-чипы непокрытых услуг — В ТОЙ ЖЕ строке состава (20.08, Саша: место важнее всего) */}
        {(() => { const rest = mc.filter((a) => !a.operatorIds.length || a.operatorIds.some((o) => !crew.includes(o))); return rest.length && !expanded ? <McMiniRow mc={rest} operators={operators} opName={boardName} candidates={mcCand} onAddOp={onAddMcOp} onRemoveOp={onRemoveMcOp} /> : null; })()}
      </div>
      <LearningRow {...peerCtx} learning={game.isLearning} />

      {expanded ? (
        <McBlock
          mc={mc}
          branchId={game.branchId}
          catalog={mcCatalog}
          operators={operators}
          candidates={mcCand}
          opById={opById} boardName={boardName}
          onAttach={onAttachMc}
          onDetach={onDetachMc}
          onAddOp={onAddMcOp}
          onRemoveOp={onRemoveMcOp}
        />
      ) : null}
    </article>
  );
}

/** Может ли оператор провести игру НА ВСЁМ её протяжении по своему графику
 *  (26.08, владелец): каждый час интервала [время, время+длительность) свободен. */
function canWholeGame(op: BoardOperator, game: BoardGame): boolean {
  return canWholeSpan(op, game.time, game.duration || 60);
}

/** «N игр» — Russian plural for the operator's game-load count. */
function gamesWord(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "игра";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "игры";
  return "игр";
}

/** Canonical form for dirty comparison: sorted op-ids per game, empties dropped. */
function normalize(map: AssignMap): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(map)) {
    if (v.length) out[k] = [...v].sort();
  }
  return out;
}
